/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3a §8] THE WORDS FOR ARCHIVING AN ORDER — SHARED, BECAUSE TWO PAGES NOW SAY THEM.
 *
 *  ⚠️ MOVED FROM `pages/ArrangementDetail/copy.ts` VERBATIM, HEADERS AND ALL. §8 puts the
 *  same act on the arrangements LIST row, and `useArchiveArrangement` is the one flow both
 *  pages call — so the vocabulary had to leave a page's own copy file for the same reason
 *  `components/fill/copy.ts` and `components/intake/copy.ts` exist. Every string below is
 *  unchanged; if one of them reads differently from before this pass, that is a bug.
 *
 *  ⚠️ AND IT IS `components/archive/`, NOT `lib/`. The hook beside it renders a dialog and
 *  calls `App.useApp()`, so it is a component-layer thing; `lib/` holds pure modules.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ARCHIVE_COPY = {
  /**
   * ⚠️ [C3a §8] THE MENU LABELS, AND THEY ARE THE SAME TWO WORDS THE DIALOG USES.
   *
   * [C1e §6b]: the control names the AXIS, the dialog names the ACT — and on a DRAFT the two
   * coincide, because `Discard` is already the builder's word for this exact endpoint
   * (`DISCARD_COPY.action`). They are declared HERE rather than read off `archiveOk`/
   * `archiveDraftOk` because a button's label and a dialog's confirm text are two decisions
   * that happen to agree today, and reading one off the other makes changing either impossible.
   */
  menuDiscard: 'Discard',
  menuArchive: 'Archive',
  menuRestore: 'Restore',
  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C1d §1] THREE TIERS, THREE SETS OF WORDS, AND THE DIALOG PICKS BY `impact.tier`.
   *
   *  CANCELLATION withdraws something that has not happened and TELLS PEOPLE, so it states
   *  the officer count. FILING puts away something that is over and tells NOBODY, so it must
   *  not state one — a sentence naming forty officers under a button called File reads as a
   *  promise that forty officers are about to hear about it.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  /**
   * ⚠️ [amended C1f §10] "DELETE" WAS WRONG FOR ALL THREE TIERS, AND IT WAS THE LAST ONE LEFT.
   *
   * [C1e §6b] corrected the toolbar — which names the AXIS, `Archive` — and reported this,
   * the dialog, which names the ACT. Nothing is deleted by any tier: an archived issued order
   * is the record of an order issued and then cancelled and is kept for ever (FR-5.2.15), and
   * an archived draft carries a `purge_at` — which, since [C5c §8], the duty-lifecycle tick acts on
   * when it passes. The dialog's words describe the ACT, and archiving itself deletes nothing.
   *
   * ⚠️ IT BRANCHES ON `isIssued`, EXACTLY AS THE SENTENCE THREE LINES BELOW ALREADY DOES.
   * The CANCELLATION tier serves two records — a draft nobody ever issued and a published
   * order that has not started — and there is nothing to WITHDRAW from a draft. `isIssued` is
   * on `DeleteImpactDto` and is already awaited before this dialog opens, so this costs no
   * new data and no new fetch.
   *
   * ⚠️ "Withdraw" IS ALREADY THIS FILE'S WORD FOR THIS ACT — `archiveDuties` says "will be
   * withdrawn", `archiveNotYoursLive` says "only the CP office can withdraw an order that is
   * still to run", and `restoreNotYours` says "an order that was withdrawn". And "Discard" is
   * the builder's own word for the same act on a draft (`DISCARD_COPY.action`). Neither is
   * invented here.
   */
  archiveTitle: 'Withdraw this order?',
  archiveOk: 'Withdraw',
  /** Tier CANCELLATION, on a DRAFT — never issued, so there is nothing to withdraw. */
  archiveDraftTitle: 'Discard this draft?',
  archiveDraftOk: 'Discard',
  archiveDuties: (duties: number): string => `${String(duties)} ${duties === 1 ? 'duty' : 'duties'} will be withdrawn.`,
  /**
   * ⚠️ [C1e §6a] TWO SENTENCES, BECAUSE THE DIALOG SERVES TWO RECORDS AND ONLY ONE IS PURGED.
   *
   * `archiveArrangement` writes `purge_at` ONLY when `origin === 'DRAFT'`, and
   * `purgeSelector` can only ever return archived DRAFTS — the literal is in its return type
   * for exactly that reason. So "restore it for the next 30 days" was FALSE for every
   * published order, which is the kind this dialog is mostly opened on: an archived issued
   * order is the record of an order issued and then cancelled, and FR-5.2.15 keeps it for
   * ever.
   *
   * ⚠️ [amended C5c §8] AND THE DRAFT'S THIRTY DAYS NOW END. Nothing acted on `purge_at` until the
   * duty-lifecycle tick's draft purge; the sentence below is now a promise that is kept rather than
   * a window that never closed.
   *
   * The CANCELLATION tier covers a draft AND a not-yet-started published order alike, so the
   * dialog has to tell them apart — and `isIssued` is on `DeleteImpactDto` already, stated
   * before the click, precisely so it can.
   */
  archiveRestorableDraft: 'It moves to the Archive, where you can restore it for the next 30 days.',
  archiveRestorableIssued:
    'It moves to the Archive, where you can restore it. An order that was issued is kept there indefinitely.',
  /** Tier FILING — the duty happened, so nothing is cancelled and nothing is sent. */
  archiveFileTitle: 'File this order?',
  archiveFileOk: 'File',
  archiveFileBody: 'Every duty on this order has ended. Filing it moves the order out of the current list.',
  archiveFileDuties: (duties: number): string =>
    `${String(duties)} ${duties === 1 ? 'duty' : 'duties'} will be filed with it.`,
  /**
   * ⚠️ FILING TELLS NOBODY, AND THE WORDS SAY SO RATHER THAN LEAVING IT TO BE INFERRED.
   * An operator who has just read "40 officers are already named on it" on the other dialog
   * will assume this one sends something unless it is denied out loud.
   */
  archiveFileSilent: 'Nobody is told: the duty is already over.',
  /**
   * ⚠️ [C1e §5] THE SERVER'S OWN TWO SENTENCES FOR A REFUSED ACTOR, said before the
   * confirmation rather than after it. They name the STATE OF THE ORDER and never the caller
   * (`DEACTIVATED`'s rule) and they say who to ask — and they disclose nothing, because
   * `GET /:id/delete-impact` already returns the tier to anyone who can open this page.
   */
  archiveNotYours:
    'Every duty on this order has ended, and filing it is done from the CP office. Ask them to file it — nothing on the order changes until they do.',
  archiveNotYoursLive:
    'This order has been issued and has not finished. Only the CP office can withdraw an order that is still to run.',
  /** Tier BLOCKED — the server's own two sentences, said before the click rather than after. */
  /* ⚠️ [amended C1f §10] "deleted" → "withdrawn". The second clause is VERBATIM: it is the
     only place in the product that tells an operator filing exists, and
     `ArrangementDetail.test.tsx` pins it deliberately. */
  archiveBlocked:
    'This cannot be withdrawn: a duty has already started and the order has not finished. ' +
    'Once every duty has ended you can file it.',
  archiveBlockedEvent: 'A duty under this order has already been carried out, so it can no longer be withdrawn.',
  restore: 'Brings this order and its duties back out of the Archive.',
  /**
   * ⚠️ [C1e §5] A RESTORE IS THE ARCHIVE RUN BACKWARDS, so it takes the same gate. Un-filing
   * an order that already ran is a CP-office nodal's to do; un-cancelling one that was
   * withdrawn puts it back in front of the divisions that were told, and stays a super
   * admin's. The sentence names the ACT rather than the caller.
   */
  restoreNotYours: 'Only the CP office can bring back an order that was withdrawn.',
  /**
   * ⚠️ [C2b §4g] MOVED HERE FROM `CORRIGENDUM_COPY` BECAUSE THE ARCHIVE FLOW IS ITS ONLY
   * READER. A cascade is named in all three archive dialogs or in none — mentioning it in
   * one teaches the operator that the other two do not cascade — and a `components/` module
   * must not import a page's copy file to say so. `unpublishCascade` is a DIFFERENT key with
   * its own counts and stays where it is, with the unpublish flow that reads it.
   *
   * ⚠️ [C2b §4g] IT NAMES THE ORDERS RATHER THAN COUNTING THEM. "and 1 other order" is a
   * sentence an operator cannot check; a PA number is one they can read off the paper in
   * front of them.
   */
  cascade: (references: readonly string[]): string =>
    `This also applies to ${references.join(', ')}, which ${references.length === 1 ? 'goes' : 'go'} with it.`,
} as const;
